<template>
  <v-card class="shadow">
    <v-card-title>
      <v-row justify="space-between" align="center">
        <v-col cols="12" sm="7">
          <div class="d-flex flex-wrap align-center" style="gap: 8px">
            <span class="text-h6 font-weight-bold mr-2">Bet &amp; Card Presets</span>

            <v-btn color="primary" depressed small @click="openCreateDialog">
              <v-icon left small>mdi-plus</v-icon>
              New Preset
            </v-btn>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="secondary"
                  :disabled="loading"
                  v-bind="attrs"
                  v-on="on"
                  @click="loadRecords"
                >
                  <v-icon>mdi-cached</v-icon>
                </v-btn>
              </template>
              <span>Refresh</span>
            </v-tooltip>
          </div>
        </v-col>

        <v-col cols="12" sm="5" md="4">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Search presets"
            single-line
            hide-details="auto"
            :disabled="loading"
            clearable
            outlined
            dense
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pb-0">
      <v-alert type="info" outlined dense class="mb-4">
        Active presets with a card quantity greater than zero are created
        automatically for every new ending game.
      </v-alert>

      <v-btn-toggle v-model="selectedType" mandatory color="primary">
        <v-btn
          v-for="option in typeOptions"
          :key="option.value"
          :value="option.value"
          small
        >
          {{ option.text }}
        </v-btn>
      </v-btn-toggle>

      <v-chip small color="primary" outlined class="ml-3">
        {{ activeCardTotal }} cards per new game
      </v-chip>
    </v-card-text>

    <v-card-text class="pa-0 pt-3">
      <v-data-table
        :headers="headers"
        :items="filteredRecords"
        :search="search"
        :loading="loading"
        :items-per-page="10"
        sort-by="amount"
        loading-text="Loading presets..."
        no-data-text="No presets found"
      >
        <template v-slot:[`item.action`]="{ item }">
          <div class="d-flex justify-center">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="openEditDialog(item)"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click="openDeleteDialog(item)"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:[`item.amount`]="{ item }">
          <span class="font-weight-medium">{{ formatAmount(item.amount) }}</span>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            small
            :class="
              item.status === 'Active'
                ? 'green lighten-5 green--text'
                : 'orange lighten-5 orange--text'
            "
          >
            {{ item.status }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog v-model="formDialog" max-width="520" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? "Edit Preset" : "New Preset" }}
          <v-spacer></v-spacer>
          <v-btn icon :disabled="saving" @click="closeFormDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-5">
          <v-alert v-if="formError" type="error" dense outlined>
            {{ formError }}
          </v-alert>

          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="submitForm"
          >
            <v-select
              v-model="form.bet_type"
              :items="typeOptions"
              item-text="text"
              item-value="value"
              label="Bet Type"
              :disabled="isEditing"
              :rules="[rules.required]"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
            ></v-select>

            <v-text-field
              ref="amount"
              v-model="form.amount"
              type="number"
              min="1"
              step="1"
              label="Bet Amount"
              :rules="[rules.required, rules.numeric, positiveAmount]"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
            ></v-text-field>

            <v-text-field
              v-model.number="form.card_quantity"
              type="number"
              min="0"
              max="1000"
              step="1"
              label="Card Quantity"
              hint="Use 0 to keep the amount available without creating cards automatically."
              persistent-hint
              :rules="[
                cardQuantityRequired,
                rules.numeric,
                nonNegativeCardQuantity,
                maximumCardQuantity,
              ]"
              outlined
              dense
              class="mb-4"
            ></v-text-field>

            <v-select
              v-model="form.status"
              :items="statusOptions"
              label="Status"
              :rules="[rules.required]"
              outlined
              dense
              hide-details="auto"
            ></v-select>

            <div class="d-flex justify-end mt-6">
              <v-btn depressed class="mr-2" :disabled="saving" @click="closeFormDialog">
                Cancel
              </v-btn>
              <v-btn color="primary" depressed type="submit" :loading="saving">
                {{ isEditing ? "Update" : "Create" }}
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="440" persistent>
      <v-card>
        <v-card-title>Delete Preset?</v-card-title>
        <v-card-text v-if="recordToDelete">
          This will remove
          <strong>{{ formatAmount(recordToDelete.amount) }}</strong>
          from the
          <strong>{{ recordToDelete.bet_type_label }}</strong>
          selection.

          <v-alert v-if="deleteError" type="error" dense outlined class="mt-4 mb-0">
            {{ deleteError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="deleting" @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn color="error" depressed :loading="deleting" @click="deleteRecord">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Snackbar ref="snackbar" />
  </v-card>
</template>

<script>
import formRules from "@/helpers/formRules";
import responseGet from "@/helpers/api_request_get";
import responsePost from "@/helpers/api_request_post";
import Snackbar from "@/components/Snackbar";

const initialForm = (betType = "per_quarter") => ({
  id: null,
  bet_type: betType,
  amount: "",
  card_quantity: 1,
  status: "Active",
});

export default {
  components: {
    Snackbar,
  },

  data: () => ({
    rules: formRules,
    valid: true,
    loading: false,
    saving: false,
    deleting: false,
    formDialog: false,
    deleteDialog: false,
    isEditing: false,
    search: "",
    selectedType: "per_quarter",
    records: [],
    form: initialForm(),
    formError: "",
    deleteError: "",
    recordToDelete: null,
    typeOptions: [
      { text: "Per Quarter", value: "per_quarter" },
      { text: "Traditional", value: "traditional" },
    ],
    statusOptions: ["Active", "Inactive"],
    headers: [
      {
        text: "Actions",
        value: "action",
        class: "text-uppercase",
        sortable: false,
        align: "center",
        width: 110,
      },
      { text: "Amount", value: "amount", class: "text-uppercase" },
      {
        text: "Card Quantity",
        value: "card_quantity",
        class: "text-uppercase",
        align: "center",
      },
      { text: "Status", value: "status", class: "text-uppercase" },
    ],
  }),

  computed: {
    filteredRecords() {
      return this.records.filter(
        (record) => record.bet_type === this.selectedType,
      );
    },
    activeCardTotal() {
      return this.filteredRecords
        .filter((record) => record.status === "Active")
        .reduce((total, record) => total + Number(record.card_quantity || 0), 0);
    },
  },

  created() {
    this.loadRecords();
  },

  methods: {
    positiveAmount(value) {
      return Number(value) > 0 || "The bet amount must be greater than zero.";
    },

    cardQuantityRequired(value) {
      return (
        (value !== "" && value !== null && value !== undefined) ||
        "This field is required."
      );
    },

    nonNegativeCardQuantity(value) {
      return Number(value) >= 0 || "The card quantity cannot be negative.";
    },

    maximumCardQuantity(value) {
      return Number(value) <= 1000 || "The card quantity cannot exceed 1,000.";
    },

    formatAmount(value) {
      return Number(value || 0).toLocaleString("en-PH");
    },

    async loadRecords() {
      try {
        this.loading = true;
        const response = await responseGet(
          "",
          "/ending/configuration/bet_amounts",
        );

        if (response.status === 200) {
          this.records = response.data.records || [];
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.$store.commit("setRefreshError", "Failed to load bet amounts.");
        }
      } catch (error) {
        this.$store.commit("setRefreshError", "Failed to load bet amounts.");
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.isEditing = false;
      this.form = initialForm(this.selectedType);
      this.formError = "";
      this.formDialog = true;
      this.resetFormValidation();
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.form = {
        id: item.id,
        bet_type: item.bet_type,
        amount: item.amount,
        card_quantity: item.card_quantity,
        status: item.status,
      };
      this.formError = "";
      this.formDialog = true;
      this.resetFormValidation();
    },

    resetFormValidation() {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }

        if (this.$refs.amount) {
          this.$refs.amount.focus();
        }
      });
    },

    closeFormDialog() {
      if (this.saving) {
        return;
      }

      this.formDialog = false;
      this.form = initialForm(this.selectedType);
      this.formError = "";
    },

    buildFormData() {
      const formData = new FormData();

      if (this.isEditing) {
        formData.append("id", this.form.id);
      }

      formData.append("bet_type", this.form.bet_type);
      formData.append("amount", this.form.amount);
      formData.append("card_quantity", this.form.card_quantity);
      formData.append("status", this.form.status);

      return formData;
    },

    firstErrorMessage(response, fallback) {
      if (!response || !response.data) {
        return fallback;
      }

      const errorData = response.data.messages || response.data;

      if (errorData && typeof errorData === "object") {
        const message = Object.values(errorData).find(
          (value) => typeof value === "string" && value.length,
        );

        if (message) {
          return message;
        }
      }

      if (typeof response.data.message === "string") {
        return response.data.message;
      }

      return fallback;
    },

    async submitForm() {
      this.formError = "";

      if (!this.$refs.form.validate()) {
        return;
      }

      const wasEditing = this.isEditing;

      try {
        this.saving = true;
        const endpoint = wasEditing
          ? "ending/configuration/bet_amounts/update"
          : "ending/configuration/bet_amounts/create";
        const response = await responsePost(this.buildFormData(), endpoint);

        if (response.status === 200) {
          this.formDialog = false;
          this.form = initialForm(this.selectedType);
          await this.loadRecords();
          this.$refs.snackbar.show(
            "success",
            "mdi-check-circle-outline",
            wasEditing
              ? "Preset updated successfully."
              : "Preset created successfully.",
            4000,
          );
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.formError = this.firstErrorMessage(
            response,
            "Failed to save preset.",
          );
        }
      } catch (error) {
        this.formError = "Failed to save preset.";
      } finally {
        this.saving = false;
      }
    },

    openDeleteDialog(item) {
      this.recordToDelete = item;
      this.deleteError = "";
      this.deleteDialog = true;
    },

    closeDeleteDialog() {
      if (this.deleting) {
        return;
      }

      this.deleteDialog = false;
      this.recordToDelete = null;
      this.deleteError = "";
    },

    async deleteRecord() {
      if (!this.recordToDelete) {
        return;
      }

      const formData = new FormData();
      formData.append("id", this.recordToDelete.id);
      formData.append("bet_type", this.recordToDelete.bet_type);

      try {
        this.deleting = true;
        const response = await responsePost(
          formData,
          "ending/configuration/bet_amounts/delete",
        );

        if (response.status === 200) {
          this.deleteDialog = false;
          this.recordToDelete = null;
          await this.loadRecords();
          this.$refs.snackbar.show(
            "success",
            "mdi-check-circle-outline",
            "Preset deleted successfully.",
            4000,
          );
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.deleteError = this.firstErrorMessage(
            response,
            "Failed to delete preset.",
          );
        }
      } catch (error) {
        this.deleteError = "Failed to delete preset.";
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
